import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="channel-backlog"
export default class extends Controller {
  static targets = [
    "spaceUrl",
    "apiKey",
    "backlogProjectId",
    "issueTypeId",
    "priorityId",
    "resolveStatusId",
    "reopenStatusId",
  ];

  static values = {
    projectId: Number,
    channelId: Number,
  };

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  initialize() {
    // Define empty change handlers for targets that don't need custom behavior.
    // The form auto-wires data-action="{controller}#{target}Changed" for each target.
    this.constructor.targets.forEach((targetName) => {
      this[`${targetName}Changed`] ||= function () {};
    });
  }

  connect() {
    this.allSelectTargets.forEach((select) => (select.disabled = true));
    this.addFetchButton();

    // Auto-fetch if editing existing channel with credentials
    if (this.hasCredentials) {
      this.loadProjects();
    }
  }

  disconnect() {
    this.fetchButton?.remove();
    this.fetchButton = null;
  }

  // ---------------------------------------------------------------------------
  // Getters
  // ---------------------------------------------------------------------------

  get hasCredentials() {
    return Boolean(this.spaceUrlTarget.value && this.apiKeyTarget.value);
  }

  get allSelectTargets() {
    return [this.backlogProjectIdTarget, ...this.dependentSelectTargets];
  }

  get dependentSelectTargets() {
    return [
      this.issueTypeIdTarget,
      this.priorityIdTarget,
      this.resolveStatusIdTarget,
      this.reopenStatusIdTarget,
    ];
  }

  // ---------------------------------------------------------------------------
  // Event Handlers
  // ---------------------------------------------------------------------------

  backlogProjectIdChanged() {
    this.clearDependentSelects();

    const projectId = this.backlogProjectIdTarget.value;
    if (projectId) {
      this.loadProjectDependentData(projectId);
    }
  }

  // ---------------------------------------------------------------------------
  // Data Loading
  // ---------------------------------------------------------------------------

  async loadProjects() {
    if (!this.hasCredentials) return;

    this.clearAlert();
    this.clearDependentSelects();
    this.setSelectLoading(this.backlogProjectIdTarget, "Loading projects...");

    await this.loadData("/projects", {}, "Select a project...", [
      this.backlogProjectIdTarget,
    ]);

    // After projects load, load dependent data if a project is selected
    const projectId = this.backlogProjectIdTarget.value;
    if (projectId) {
      this.loadProjectDependentData(projectId);
    }
  }

  loadProjectDependentData(projectId) {
    if (!projectId || !this.hasCredentials) return;

    this.setSelectLoading(this.issueTypeIdTarget, "Loading issue types...");
    this.loadData(
      "/issue_types",
      { backlog_project_id: projectId },
      "Select an issue type...",
      [this.issueTypeIdTarget],
    );

    this.setSelectLoading(this.resolveStatusIdTarget, "Loading statuses...");
    this.setSelectLoading(this.reopenStatusIdTarget, "Loading statuses...");
    this.loadData(
      "/statuses",
      { backlog_project_id: projectId },
      "Select a status...",
      [this.resolveStatusIdTarget, this.reopenStatusIdTarget],
    );

    this.setSelectLoading(this.priorityIdTarget, "Loading priorities...");
    this.loadData("/priorities", {}, "Select a priority...", [
      this.priorityIdTarget,
    ]);
  }

  async loadData(endpoint, extraParams, placeholder, selectElements) {
    const url = this.buildUrl(endpoint, extraParams);

    try {
      const response = await fetch(url, {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw { response, data };
      }

      const items = await response.json();
      selectElements.forEach((select) => {
        this.populateSelect(select, items, placeholder);
      });
    } catch (error) {
      this.reportError(error);
    }
  }

  // ---------------------------------------------------------------------------
  // URL Building
  // ---------------------------------------------------------------------------

  buildUrl(path, extraParams = {}) {
    const params = new URLSearchParams({
      space_url: this.spaceUrlTarget.value,
      api_key: this.apiKeyTarget.value,
      ...extraParams,
    });

    if (this.channelIdValue) {
      params.set("channel_id", this.channelIdValue);
    }

    return `/projects/${this.projectIdValue}/channels/backlog${path}?${params}`;
  }

  // ---------------------------------------------------------------------------
  // DOM Helpers
  // ---------------------------------------------------------------------------

  addFetchButton() {
    if (this.fetchButton) return;

    this.fetchButton = document.createElement("button");
    this.fetchButton.textContent = "Fetch Projects";
    this.fetchButton.className = "btn btn-sm btn-soft-primary mt-2";
    this.fetchButton.id = "fetch_backlog_projects";
    this.fetchButton.type = "button";
    this.fetchButton.addEventListener("click", (e) => {
      e.preventDefault();
      this.loadProjects();
    });

    this.apiKeyTarget.closest(".mb-3").appendChild(this.fetchButton);
  }

  clearDependentSelects() {
    this.dependentSelectTargets.forEach((select) => {
      select.innerHTML = "";
      select.disabled = true;
    });
  }

  setSelectLoading(select, message) {
    select.disabled = true;
    select.innerHTML = "";
    select.appendChild(this.createOption(message));
  }

  populateSelect(select, items, placeholder) {
    const selectedValue = select.getAttribute("value");
    select.innerHTML = "";
    select.appendChild(this.createOption(placeholder || "Select..."));

    // API returns [name, id] pairs
    items.forEach(([text, value]) => {
      select.appendChild(
        this.createOption(text, value, value === selectedValue),
      );
    });

    select.disabled = false;
  }

  createOption(text, value = "", selected = false) {
    const option = document.createElement("option");
    option.value = value;
    option.textContent = text;
    option.selected = selected;
    return option;
  }

  // ---------------------------------------------------------------------------
  // Error Handling
  // ---------------------------------------------------------------------------

  reportError(error) {
    let message = "An error occurred loading Backlog data";

    if (error.data?.error) {
      message = `Backlog error: ${error.data.error}`;
    } else if (error.response?.status === 403) {
      message = "Backlog access denied: Invalid API Key";
    } else if (error.response?.status === 401) {
      message = "Backlog access denied: Invalid credentials";
    }

    this.showAlert(message);
  }

  showAlert(message) {
    this.clearAlert();

    this.alertElement = document.createElement("div");
    this.alertElement.className =
      "alert alert-danger d-flex align-items-center";
    this.alertElement.setAttribute("role", "alert");
    this.alertElement.id = "backlog-error-alert";
    this.alertElement.innerHTML = `
      <i class="fa fa-exclamation-triangle flex-shrink-0 me-2" aria-label="Error:"></i>
      <div class="flex-grow-1">${this.escapeHtml(message)}</div>
      <button type="button" class="btn-close" aria-label="Close"></button>
    `;

    this.alertElement
      .querySelector(".btn-close")
      .addEventListener("click", () => this.clearAlert());

    const projectField = this.backlogProjectIdTarget.closest(".mb-3");
    projectField.parentNode.insertBefore(this.alertElement, projectField);
  }

  clearAlert() {
    this.alertElement?.remove();
    this.alertElement = null;
    document.getElementById("backlog-error-alert")?.remove();
  }

  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
}
